import {
  useObservabilityConfig,
  usePerformanceMonitorTrack,
  useProject,
} from '@sail/observability';
import {useMemoizedValue, useUpdatedRef} from '@sail/react';
import {useCallback, useEffect, useRef, useState} from 'react';
import useLogFetch from 'src/internal/fetch/useLogFetch';
import fullFetch from 'src/internal/shared/fetch/fullFetch';
import {
  REQUEST_SOURCE_HEADER,
  getRequestSourceHeader,
  getRequestSourceInformation,
} from 'src/internal/shared/requestSource';
import useDataConfig from 'src/public/useDataConfig';

import type {LazyFetchState} from 'src/internal/fetch/types';
import type {
  FetchHookOptions,
  FetchState,
  FetchUrl,
} from 'src/internal/shared/fetch/types';

type FetchStateWithoutCalled<TData> = Omit<FetchState<TData>, 'called'>;

const initial: FetchStateWithoutCalled<any> = {
  loading: false,
  error: undefined,
  status: undefined,
  headers: undefined,
  data: undefined,
};

const loading: FetchStateWithoutCalled<any> = {
  loading: true,
  error: undefined,
  status: undefined,
  headers: undefined,
  data: undefined,
};

export default function useLazyBaseFetch<TData = unknown>(
  url: FetchUrl,
  options: FetchHookOptions<TData>,
  format: 'text' | 'json',
): LazyFetchState<TData> {
  const dataConfig = useDataConfig();
  const optionsMemo = useMemoizedValue(options);
  const fetchRef = useUpdatedRef(dataConfig.fetch ?? globalThis.fetch);
  const logFetch = useLogFetch(dataConfig.observability);
  const project = useProject();
  const service = useObservabilityConfig()?.service;
  const performanceMonitorTrack = usePerformanceMonitorTrack();

  const [value, setValue] = useState<FetchStateWithoutCalled<TData>>(initial);
  const [called, setCalled] = useState(0);

  // eslint-disable-next-line react-hooks/refs
  if (useRef(options.retry).current !== options.retry) {
    throw new ReferenceError(
      'You are changing the reference of the `retry` function across renders, which is not allowed to prevent infinite re-renders',
    );
  }

  const requestSourceInformation = getRequestSourceInformation({
    service,
    project,
    component: optionsMemo.component,
  });

  const requestSource = getRequestSourceHeader(requestSourceInformation);

  useEffect(() => {
    if (called) {
      const abortController = new AbortController();
      const fetch = fetchRef.current;
      const end = performanceMonitorTrack(`Fetch: ${url}`);

      (async (): Promise<void> => {
        const fullOptions = {
          ...optionsMemo,

          headers: {
            [REQUEST_SOURCE_HEADER]: requestSource,
            ...optionsMemo.headers,
          },

          signal: abortController.signal,
        };

        setValue(loading);

        const newValue = await fullFetch(
          url,
          fullOptions,
          optionsMemo.prepareFetch ? optionsMemo.prepareFetch(fetch) : fetch,
          format,
          logFetch,
        );

        end();

        if (newValue !== null) {
          setValue(newValue);
        }
      })();

      return (): void => {
        end();
        abortController.abort();
      };
    }
  }, [
    called,
    fetchRef,
    url,
    optionsMemo,
    format,
    logFetch,
    requestSource,
    performanceMonitorTrack,
  ]);

  const callback = useCallback(() => {
    setCalled((called) => called + 1);
    setValue(loading);
  }, []);

  return [callback, useMemoizedValue({called: called > 0, ...value})];
}
